import { IMedicalServiceEtlField, medicalServiceEtlFieldService } from 'features/settings';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { examService, laboratoryService } from 'shared/services';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';

const CORE_ETL_FIELDS = [
  'quantity',
  'exam_code',
  'service_date',
  'request_date',
  'regional_code',
  'req_department',
  'req_laboratory',
  'exec_laboratory',
  'workstation'
];

const REQUIRED_CONTROL_FIELDS = [
  'service_date',
  'req_department',
  'exec_laboratory',
  'quantity',
  'request_date'
];

export const useMedicalServiceMetadata = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [exams, setExams] = useState<Option[]>([]);
  const [exam, setExam] = useState<Option>();
  const [laboratories, setLaboratories] = useState<Option[]>([]);
  const [lab, setLab] = useState<Option>();
  const [extraFields, setExtraFields] = useState<IMedicalServiceEtlField[]>([]);
  const [etlHeaderNames, setEtlHeaderNames] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    const fetchMetadata = async () => {
      dispatch(loadingActions.startLoading());
      try {
        const [examsData, labsData] = await Promise.all([
          examService.getAllBaseInformation(),
          laboratoryService.getAllBaseInformation()
        ]);
        setExams(examsData);
        setLaboratories(labsData);

        const res = await medicalServiceEtlFieldService.getAllPaginated();
        if (res?.results?.length > 0) {
          const headers: Record<string, string | undefined> = {};
          const extras: IMedicalServiceEtlField[] = [];

          res.results.forEach((field) => {
            if (CORE_ETL_FIELDS.includes(field.field_to)) {
              headers[field.field_to] = field.transcode;
            } else {
              extras.push(field);
            }
          });

          setEtlHeaderNames(headers);
          setExtraFields(extras);
        }
      } catch (error) {
        toast.error(t('global.error-loading-data'));
      } finally {
        dispatch(loadingActions.stopLoading());
      }
    };

    fetchMetadata();
  }, [dispatch, t]);

  /**
   * Controlla se ci sono transcodifiche mancanti tra i campi richiesti.
   * @returns Un array contenente i nomi dei campi che non hanno una transcodifica impostata.
   */
  const checkMissingTranscodes = (): string[] => {
    return REQUIRED_CONTROL_FIELDS.filter(
      (field) => !etlHeaderNames[field] || etlHeaderNames[field]?.trim() === ''
    );
  };

  return {
    exams,
    exam,
    setExam,
    laboratories,
    lab,
    setLab,
    extraFields,
    etlHeaderNames,
    checkMissingTranscodes
  };
};
